/** Upload + keystone + calibration dialog for the photo-tracing feature
 *  (issues #484, #486, #487; parent #75).
 *
 *  A stepped wizard:
 *
 *  1. Pick — a drop-target (click-to-browse or drag-and-drop); picking a
 *     file shows the image immediately AND starts the upload to the
 *     image API in parallel.
 *  2. Keystone — the photo with four draggable corner handles, and a
 *     live de-skewed preview below (#486).
 *  3. Calibrate — one horizontal and one vertical reference line over
 *     the de-skewed image; the user drags each line's ends onto
 *     something of known length and types the real-world millimetres,
 *     giving the image's mm-per-pixel scale on both axes (#487).
 *  4. Crop — a corner-draggable rectangle over the de-skewed image
 *     trims away everything the user doesn't need (#488); the region
 *     outside the crop is dimmed.
 *
 *  #489 places the result on the canvas. When the visitor is signed in
 *  the picked file uploads to the image API; otherwise it stays local to
 *  the device and the placed image references the local object URL (#496).
 */

import {
  applyMatrix3,
  cropPreimageQuad,
  defaultQuad,
  deskewSize,
  fullDeskewBounds,
  unitSquareToQuad,
  warpQuadToRect,
  type Point,
  type Quad,
} from "../tracing/keystone";
import { mmInchInputs } from "./PropertiesPanel";
import {
  DEFAULT_TRACING_IMAGE_OPACITY,
  projectTracingImages,
  type TracingImage,
} from "../model/Project";
import { newId } from "../model/ids";
import {
  prepareTracingUpload,
  renderTracingImageFinal,
} from "../tracing/loadTracingImage";
import { showCanvasToast } from "../canvasToast";
import { isFeatureEnabled } from "../featureFlags";
import {
  readPreferenceEntry,
  removePreferenceEntry,
  writePreferenceEntry,
} from "../preferences";
import type { EditorContext } from "../tools/EditorContext";
import cameraRotateRaw from "../icons/camera-rotate.svg?raw";
import cameraRaw from "../icons/camera.svg?raw";
import { stripIconSvg } from "../icons/stripIconSvg";

// The dialog's two glyphs. Its markup is a template literal built in TS, so
// the `<!-- svg:… -->` markers index.html uses aren't available — these go
// through the same shared normaliser instead (#216).
const CAMERA_ICON = stripIconSvg(cameraRaw);
const CAMERA_ROTATE_ICON = stripIconSvg(cameraRotateRaw);

const IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/avif",
]);

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** Long-side cap for the in-memory sampling raster. Warping is per-pixel
 *  JS — sampling the full camera resolution would burn main-thread time
 *  for no visible gain at preview size. The full-resolution warp happens
 *  once, at the end of the pipeline (#489). */
const SAMPLE_MAX_DIMENSION = 1200;

/** Long-side cap for the de-skewed keystone preview canvas. */
const PREVIEW_MAX_DIMENSION = 480;

/** Long-side cap for the keystone quad's rectified rectangle. */
const DESKEW_MAX_DIMENSION = 1000;

/** Long-side cap for the FULL de-skewed frame the calibration step
 *  shows (the whole photo rectified, not just the quad — #492). */
const FULL_DESKEW_MAX_DIMENSION = 1400;

type Step = "pick" | "camera" | "keystone" | "calibrate" | "crop";

/** A reference line: two draggable endpoints in deskew-raster coords. */
type RefLine = [Point, Point];

/** Crop region in deskew-raster coords. */
interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Smallest croppable side, in deskew-raster pixels — keeps the corner
 *  handles from collapsing onto each other. */
const MIN_CROP_SIDE = 24;

/** Fraction trimmed from each side for the crop's INITIAL placement
 *  (issue #508). Starting inset rather than flush to the frame keeps the
 *  corner handles off the image edges so they're easy to grab. */
const CROP_DEFAULT_INSET = 0.1;

/** Magnifier loupe (issue #497): a fixed-size square canvas shown left of
 *  the keystone / calibrate view that zooms 5× around the handle being
 *  placed, with a crosshair on the exact point. The backing size matches
 *  the markup's `width`/`height`. */
const LOUPE_SIZE = 160;
const LOUPE_ZOOM = 5;

/** Long-side cap for the source image embedded in the project when the
 *  user isn't signed in (issue #496). Matches the render-time source cap
 *  (`SOURCE_MAX_DIMENSION` in tracing/loadTracingImage.ts) so the embedded
 *  copy is no lower-resolution than what the on-canvas warp ever samples,
 *  while keeping the data URL — and the autosave / `.leather` file it
 *  lands in — bounded. */
const LOCAL_SOURCE_MAX_DIMENSION = 2000;

/** Read a blob into a self-contained `data:` URL (issue #496/#500) — used
 *  to embed the final copy in the project when the user isn't signed in. */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(blob);
  });
}

export interface UploadedImage {
  /** R2 object key, `<account>/<uuid>` — account "0" until #485. */
  key: string;
  /** Same-origin URL the stored image is served from. */
  url: string;
}

/** Stored record of an upload that landed on the server but was
 *  never added to the canvas (issue #543). Not a user preference — a
 *  transient receipt — so it goes through the raw storage seam rather than the
 *  typed registry (#998). Written when the background
 *  upload succeeds, cleared on commit; while present, an accidental page
 *  reload re-opens the dialog with the uploaded photo restored instead of
 *  the file being lost server-side. A deliberate dialog close deletes the
 *  server object AND the record. */
interface PendingUpload extends UploadedImage {
  fileName: string;
}

const PENDING_UPLOAD_KEY = "leather-art:pending-image-upload";

function readPendingUpload(): PendingUpload | null {
  const raw = readPreferenceEntry(PENDING_UPLOAD_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingUpload>;
    return typeof parsed.key === "string" &&
      typeof parsed.url === "string" &&
      typeof parsed.fileName === "string"
      ? (parsed as PendingUpload)
      : null;
  } catch {
    return null;
  }
}

function writePendingUpload(entry: PendingUpload | null): void {
  // Storage disabled is survivable here — the delete-on-close path still runs
  // in-session; only the after-a-reload rescue is lost.
  if (entry) writePreferenceEntry(PENDING_UPLOAD_KEY, JSON.stringify(entry));
  else removePreferenceEntry(PENDING_UPLOAD_KEY);
}

/** Fire-and-forget server-side delete of an abandoned upload (#543).
 *  Best-effort: a failed delete just leaves the object for a later
 *  cleanup; nothing user-visible depends on it. */
function deleteUploadedImage(key: string): void {
  void fetch(`/api/images/${key}`, { method: "DELETE" }).catch(() => {});
}

export class ImageUploadDialog {
  private host: HTMLElement;
  private ctx: EditorContext;
  /** Object URL of the locally-picked file, revoked on close/re-pick. */
  private previewUrl: string | null = null;
  /** Picked file's name — seeds the tracing image's parts-list label. */
  private fileName: string | null = null;
  /** Monotonic pick counter so a stale upload response (the user picked a
   *  different file while the first was in flight) can't clobber the
   *  status of the current one. */
  private pickSeq = 0;
  private uploaded: UploadedImage | null = null;

  /** Whether a signed-in account backs this session (issue #496). When
   *  false, traced images are kept local to the device — the R2 upload is
   *  skipped and the committed image references the local object URL. */
  private loggedIn = false;
  /** Whether this session stores images on the server: signed in AND the
   *  "Server image storage" feature flag is on (#536). Every storage
   *  decision (background original upload, commit URL, final-copy upload)
   *  branches on this — `loggedIn` alone only picks the status wording.
   *  With the flag off, a signed-in user gets the same fully-local
   *  handling as an anonymous one: data-URL embeds, nothing sent to the
   *  server. */
  private serverStorage = false;
  /** In-flight (or settled) `/api/me` probe, started on open. */
  private loginChecked: Promise<boolean> | null = null;

  private step: Step = "pick";

  /** Keystone stage state (issue #486). The quad lives in the sampling
   *  raster's coordinate space. */
  private quad: Quad | null = null;
  private sample: ImageData | null = null;
  private warpQueued = false;

  /** Calibration stage state (issue #487), in deskew-raster coords. */
  private deskew: HTMLCanvasElement | null = null;
  /** How the full de-skewed frame maps back to the quad's rectified
   *  rectangle (issue #492): display px = (rectified px − min) × scale. */
  private deskewLayout: {
    scale: number;
    minX: number;
    minY: number;
    rectWidth: number;
    rectHeight: number;
  } | null = null;
  private hLine: RefLine | null = null;
  private vLine: RefLine | null = null;
  private hMm: number | null = null;
  private vMm: number | null = null;

  /** Crop stage state (issue #488), in deskew-raster coords. */
  private crop: CropRect | null = null;

  /** Live camera stream while the camera view is open (issue #955).
   *  Stopped on capture, Back, and close so the webcam light never
   *  outlives the view. */
  private cameraStream: MediaStream | null = null;
  /** Preview / capture rotation in degrees (0 / 90 / 180 / 270). This is
   *  OUR transform, not a camera constraint: cameras only offer their
   *  native (usually landscape) modes and constraint-based portrait
   *  requests silently no-op on hardware without rotated modes. Rotating
   *  the preview via CSS and the captured frame via canvas works
   *  identically on every camera — the fit for a sideways or
   *  upside-down workbench mount. */
  private cameraRotation: 0 | 90 | 180 | 270 = 0;

  /** Fired right after a traced image is committed to the project
   *  (issue #511) so the app can drop the user into the Construction
   *  Select sub-mode — the freshly-placed image is then immediately
   *  movable, even on a project with no other construction objects. */
  private onAdded?: () => void;

  constructor(host: HTMLElement, ctx: EditorContext, onAdded?: () => void) {
    this.host = host;
    this.ctx = ctx;
    this.onAdded = onAdded;
  }

  open() {
    this.host.classList.add("open");
    this.host.innerHTML = "";
    this.uploaded = null;
    // The markup is rebuilt each open — reset the preview rotation with
    // it (#955).
    this.cameraRotation = 0;
    // Re-probe login each open — the user may have signed in (another tab,
    // the access gateway) since last time. `serverStorage` is re-derived by
    // the probe (login state + the #536 flag, which the developer panel can
    // flip between opens).
    this.loginChecked = null;
    this.serverStorage = false;
    void this.ensureLoginChecked();
    this.step = "pick";
    this.quad = null;
    this.sample = null;
    this.deskew = null;
    this.hLine = null;
    this.vLine = null;
    this.hMm = null;
    this.vMm = null;
    this.crop = null;
    const modal = document.createElement("div");
    modal.className = "modal modal-wide";
    modal.innerHTML = `
      <header>Trace an image</header>
      <div class="body">
        <div class="image-drop-zone" data-act="pick" tabindex="0" role="button"
             aria-label="Upload an image — click to browse or drop a file">
          <div class="image-drop-hint">
            <strong>Drop a photo here</strong> or click to browse.<br/>
            PNG, JPEG, WebP, GIF, or AVIF — up to 20&nbsp;MB.
          </div>
        </div>
        <div class="image-pick-actions">
          <button class="btn camera-open-btn" type="button" data-act="camera">
            ${CAMERA_ICON}
            Use camera
          </button>
        </div>
        <div class="camera-stage" hidden>
          <div class="trace-step-help">
            Point the camera at your work — a camera mounted over the
            workbench works great. Pick the device, then capture.
          </div>
          <div class="camera-controls">
            <select class="camera-device" aria-label="Camera"></select>
            <button class="btn camera-rotate-btn" type="button" data-act="rotate"
                    title="Rotate the preview 90° — for a sideways or upside-down mounted camera">
              ${CAMERA_ROTATE_ICON}
              Rotate
            </button>
          </div>
          <div class="camera-preview-box">
            <video class="camera-video" autoplay playsinline muted></video>
          </div>
        </div>
        <div class="keystone-stage" hidden>
          <div class="trace-step-help">
            Drag the four corners onto something rectangular in the photo —
            the sheet, the mat, or the piece itself. The magnifier on the
            left shows a close-up of the corner you're placing.
          </div>
          <div class="trace-loupe-row">
            <canvas class="trace-loupe keystone-loupe" width="160" height="160"></canvas>
            <div class="keystone-source">
              <img class="keystone-image" alt="Uploaded photo" />
              <svg class="keystone-overlay">
                <polygon class="keystone-quad" />
                <circle class="keystone-handle" data-corner="0" r="9" />
                <circle class="keystone-handle" data-corner="1" r="9" />
                <circle class="keystone-handle" data-corner="2" r="9" />
                <circle class="keystone-handle" data-corner="3" r="9" />
              </svg>
            </div>
          </div>
          <div class="trace-preview-label">De-skewed preview</div>
          <canvas class="keystone-preview"></canvas>
        </div>
        <div class="calibrate-stage" hidden>
          <div class="trace-step-help">
            Drag each line's ends onto something whose real size you know —
            a ruler, the mat's grid, the sheet's edge — then enter the two
            lengths.
          </div>
          <div class="trace-loupe-row">
            <canvas class="trace-loupe calibrate-loupe" width="160" height="160"></canvas>
            <div class="calibrate-source">
              <canvas class="calibrate-image"></canvas>
              <svg class="calibrate-overlay">
                <line class="calibrate-line calibrate-line-h" />
                <circle class="calibrate-handle calibrate-handle-h" data-line="h" data-end="0" r="9" />
                <circle class="calibrate-handle calibrate-handle-h" data-line="h" data-end="1" r="9" />
                <line class="calibrate-line calibrate-line-v" />
                <circle class="calibrate-handle calibrate-handle-v" data-line="v" data-end="0" r="9" />
                <circle class="calibrate-handle calibrate-handle-v" data-line="v" data-end="1" r="9" />
              </svg>
            </div>
          </div>
          <div class="calibrate-fields">
            <div class="field calibrate-field calibrate-field-h">
              <label>Horizontal line</label>
            </div>
            <div class="field calibrate-field calibrate-field-v">
              <label>Vertical line</label>
            </div>
          </div>
          <div class="calibrate-scale" role="status"></div>
        </div>
        <div class="crop-stage" hidden>
          <div class="trace-step-help">
            Drag the corners to trim away everything you don't need; drag
            inside the box to move it.
          </div>
          <div class="crop-source">
            <canvas class="crop-image"></canvas>
            <svg class="crop-overlay">
              <path class="crop-shade" fill-rule="evenodd" />
              <rect class="crop-rect" />
              <circle class="crop-handle" data-corner="0" r="9" />
              <circle class="crop-handle" data-corner="1" r="9" />
              <circle class="crop-handle" data-corner="2" r="9" />
              <circle class="crop-handle" data-corner="3" r="9" />
            </svg>
          </div>
        </div>
        <div class="image-upload-status" role="status"></div>
      </div>
      <footer>
        <button class="btn" data-act="repick" hidden>Choose a different photo</button>
        <button class="btn" data-act="back" hidden>Back</button>
        <button class="btn primary" data-act="capture" hidden>Capture photo</button>
        <button class="btn primary" data-act="next" hidden>Next</button>
        <button class="btn primary" data-act="add" hidden>Add to canvas</button>
        <button class="btn" data-act="close">Close</button>
      </footer>
    `;
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = [...IMAGE_TYPES].join(",");
    fileInput.hidden = true;
    fileInput.addEventListener("change", () => {
      const f = fileInput.files?.[0];
      if (f) this.handleFile(f);
    });
    modal.appendChild(fileInput);

    const zone = modal.querySelector(".image-drop-zone") as HTMLElement;
    zone.addEventListener("click", () => fileInput.click());
    zone.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        fileInput.click();
      }
    });
    zone.addEventListener("dragover", (ev) => {
      ev.preventDefault();
      zone.classList.add("drag-over");
    });
    zone.addEventListener("dragleave", () => {
      zone.classList.remove("drag-over");
    });
    zone.addEventListener("drop", (ev) => {
      ev.preventDefault();
      zone.classList.remove("drag-over");
      const f = ev.dataTransfer?.files?.[0];
      if (f) this.handleFile(f);
    });

    modal.addEventListener("click", (ev) => {
      const btn = (ev.target as HTMLElement).closest(
        "[data-act]",
      ) as HTMLButtonElement | null;
      if (!btn) return;
      switch (btn.dataset.act) {
        case "close":
          this.close();
          break;
        case "repick":
          fileInput.click();
          break;
        case "camera":
          void this.enterCameraStage();
          break;
        case "capture":
          void this.captureCameraFrame();
          break;
        case "rotate":
          this.rotateCameraPreview();
          break;
        case "back":
          if (this.step === "calibrate") this.setStep("keystone");
          else if (this.step === "crop") this.setStep("calibrate");
          else if (this.step === "camera") {
            this.stopCameraStream();
            this.setStep("pick");
          }
          break;
        case "next":
          if (this.step === "keystone") this.enterCalibrateStage();
          else if (this.step === "calibrate") this.enterCropStage();
          break;
        case "add":
          this.addToCanvas();
          break;
      }
    });

    this.wireKeystoneHandles(modal);
    this.wireCalibrateHandles(modal);
    this.wireCalibrateInputs(modal);
    this.wireCropHandles(modal);

    this.host.appendChild(modal);
    this.host.addEventListener("click", this.handleBackdrop);
  }

  /** Toggle the per-step blocks and footer buttons. */
  private setStep(step: Step) {
    this.step = step;
    const q = (sel: string) =>
      this.host.querySelector(sel) as HTMLElement | null;
    const zone = q(".image-drop-zone");
    const pickActions = q(".image-pick-actions");
    const camera = q(".camera-stage");
    const keystone = q(".keystone-stage");
    const calibrate = q(".calibrate-stage");
    const crop = q(".crop-stage");
    const repick = q("[data-act='repick']");
    const back = q("[data-act='back']");
    const capture = q("[data-act='capture']");
    const next = q("[data-act='next']");
    if (zone) zone.hidden = step !== "pick";
    if (pickActions) pickActions.hidden = step !== "pick";
    if (camera) camera.hidden = step !== "camera";
    if (keystone) keystone.hidden = step !== "keystone";
    if (calibrate) calibrate.hidden = step !== "calibrate";
    if (crop) crop.hidden = step !== "crop";
    // The camera view is a pick-step variant — no photo exists yet, so
    // "Choose a different photo" would be misleading there.
    if (repick) repick.hidden = step === "pick" || step === "camera";
    if (back)
      back.hidden =
        step !== "calibrate" && step !== "crop" && step !== "camera";
    if (capture) capture.hidden = step !== "camera";
    if (next) next.hidden = step !== "keystone" && step !== "calibrate";
    this.syncNextButton();
    this.syncAddButton();
  }

  /** The calibrate step can't advance to crop until both axes have a
   *  drawn line and an entered length (issue #499) — the placement is
   *  sized in real mm, so an uncalibrated image has no meaningful size.
   *  The keystone step's Next is always enabled (a quad always exists). */
  private syncNextButton() {
    const next = this.host.querySelector(
      "[data-act='next']",
    ) as HTMLButtonElement | null;
    if (!next) return;
    if (this.step !== "calibrate") {
      next.disabled = false;
      next.title = "";
      return;
    }
    const { x, y } = this.calibrationScale();
    const calibrated = x !== null && y !== null;
    next.disabled = !calibrated;
    next.title = calibrated
      ? "Crop the de-skewed image"
      : "Enter both calibration lengths first";
  }

  /** Whether an image source is ready to commit: the landed upload (signed
   *  in) or the locally-picked photo (not, issue #496). The actual local
   *  data URL is generated lazily at commit time — see localImageDataUrl. */
  private hasImageSource(): boolean {
    return (
      this.uploaded !== null ||
      (!this.serverStorage && this.previewUrl !== null)
    );
  }

  /** Encode the picked photo as a self-contained `data:` URL so a
   *  not-signed-in user's tracing image survives reload and travels inside
   *  the saved `.leather` file (issue #496) — a `blob:` object URL would be
   *  dead the moment the dialog closes. Snapshots the already-loaded
   *  keystone `<img>` (natural resolution) into a canvas capped to
   *  LOCAL_SOURCE_MAX_DIMENSION and re-encodes it; the stored `quad` is
   *  normalized to the full source, so this embeds the WHOLE photo, not the
   *  crop. WebP keeps it compact; `toDataURL` falls back to PNG where WebP
   *  encoding isn't supported. */
  private localImageDataUrl(): string | null {
    const img = this.host.querySelector(
      ".keystone-image",
    ) as HTMLImageElement | null;
    if (!img || !img.naturalWidth || !img.naturalHeight) return null;
    const scale = Math.min(
      1,
      LOCAL_SOURCE_MAX_DIMENSION /
        Math.max(img.naturalWidth, img.naturalHeight),
    );
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
    const webp = canvas.toDataURL("image/webp", 0.85);
    if (webp.startsWith("data:image/webp")) return webp;
    // Browsers without a WebP encoder (Safari) quietly hand back PNG —
    // several times the size for a photo. JPEG is the modern-enough
    // fallback there (#547): tracing photos have no alpha to lose.
    return canvas.toDataURL("image/jpeg", 0.85);
  }

  /** The crop step's "Add to canvas" needs an image source — the landed
   *  upload (signed in) or the locally-picked photo (not, issue #496) —
   *  plus both calibration lengths (the placement is sized in real mm). */
  private syncAddButton() {
    const add = this.host.querySelector(
      "[data-act='add']",
    ) as HTMLButtonElement | null;
    if (!add) return;
    add.hidden = this.step !== "crop";
    const { x, y } = this.calibrationScale();
    const calibrated = x !== null && y !== null;
    const ready = calibrated && this.hasImageSource();
    add.disabled = !ready;
    add.title = ready
      ? "Place the cropped image on the canvas"
      : !calibrated
        ? "Go Back and enter both calibration lengths first"
        : "Wait for the upload to finish (or re-pick the photo if it failed)";
  }

  /** Commit the traced image to the project (issue #489): the crop
   *  window's preimage quad (normalized to the source) plus the
   *  calibrated physical size, placed at the mat origin below all other
   *  content. */
  private addToCanvas() {
    if (
      !this.quad ||
      !this.sample ||
      !this.deskew ||
      !this.deskewLayout ||
      !this.crop
    )
      return;
    const { x: mmPerPxX, y: mmPerPxY } = this.calibrationScale();
    // Server storage (signed in + flag on, #536) → the uploaded R2 URL;
    // otherwise → embed the photo as a self-contained data URL so it
    // persists with the project (issue #496).
    const url = this.uploaded
      ? this.uploaded.url
      : this.serverStorage
        ? null
        : this.localImageDataUrl();
    if (mmPerPxX === null || mmPerPxY === null || !url) return;
    // The crop lives in the FULL de-skewed frame's display pixels (#492);
    // cropPreimageQuad needs it in the quad's rectified-rectangle space,
    // so undo the frame's scale and origin shift first.
    const { scale, minX, minY, rectWidth, rectHeight } = this.deskewLayout;
    const effective = cropPreimageQuad(
      this.quad,
      {
        x: this.crop.x / scale + minX,
        y: this.crop.y / scale + minY,
        w: this.crop.w / scale,
        h: this.crop.h / scale,
      },
      rectWidth,
      rectHeight,
    );
    const { width: sw, height: sh } = this.sample;
    const image: TracingImage = {
      id: newId("timg"),
      name: this.fileName?.replace(/\.[a-z0-9]+$/i, "") || "Traced image",
      url,
      quad: effective.map((p) => ({ x: p.x / sw, y: p.y / sh })),
      widthMm: this.crop.w * mmPerPxX,
      heightMm: this.crop.h * mmPerPxY,
      x: 0,
      y: 0,
      opacity: DEFAULT_TRACING_IMAGE_OPACITY,
    };
    // Hand the cropped de-skew preview to the Scene (issue #510) so the
    // image shows at its target position the instant it's placed —
    // before the full-resolution warp (an async fetch + per-pixel warp)
    // finishes. The crop rect is already in `this.deskew` pixel coords.
    this.primeCanvasPreview(image.id);
    this.ctx.commit("Add traced image", (proj) => ({
      ...proj,
      tracingImages: [...projectTracingImages(proj), image],
    }));
    // Render + store the 200-DPI final copy next to the original (issue
    // #500). Runs after placement so the image appears immediately; the
    // result is patched in silently when it lands. Capture serverStorage
    // now — close() runs synchronously right after and the produce is
    // async.
    void this.produceFinalCopy(image, this.serverStorage);
    showCanvasToast(`"${image.name}" added below the drawing`);
    // The upload now has an owner (the committed tracing image) — clear it
    // so close() below doesn't treat it as abandoned and delete it (#543).
    this.uploaded = null;
    writePendingUpload(null);
    this.close();
    // Hand control to the Select sub-mode so the just-placed image can
    // be moved right away (issue #511).
    this.onAdded?.();
  }

  /** Build the cropped preview from the de-skewed canvas + crop rect
   *  (both in deskew pixel coords) and hand it to the Scene so the just-
   *  placed image renders immediately at its target bounds while the
   *  full warp derives (issue #510). Best-effort — any failure just
   *  falls back to the Scene's placeholder rectangle. */
  private primeCanvasPreview(imageId: string) {
    if (!this.deskew || !this.crop) return;
    try {
      const w = Math.max(1, Math.round(this.crop.w));
      const h = Math.max(1, Math.round(this.crop.h));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas
        .getContext("2d")!
        .drawImage(
          this.deskew,
          this.crop.x,
          this.crop.y,
          this.crop.w,
          this.crop.h,
          0,
          0,
          w,
          h,
        );
      this.ctx.scene.primeTracingImagePreview(imageId, canvas);
    } catch {
      // Ignore — the Scene draws a placeholder rectangle instead.
    }
  }

  /** Render the de-skewed + cropped final copy at 200 DPI (AVIF where the
   *  browser can encode it) and record it on the placed image as the
   *  thing the canvas actually loads (issue #500). With server storage
   *  (signed in + flag on, #536) it's uploaded next to the original;
   *  otherwise it's embedded as a `data:` URL so it stays with the
   *  project (issue #496) — and the ORIGINAL's embedded data URL is
   *  dropped in the same patch: without server storage only the
   *  projected copy is kept, so the (much larger) original never sits in
   *  the autosaved project once the proxy exists. `url` is repointed at
   *  the final copy and `quad` reset to the identity frame, keeping the
   *  `url` + `quad` re-derive fallback geometrically correct (the final
   *  copy IS the whole de-skewed crop). Best-effort: on any failure the
   *  image keeps working from the original `url` + `quad`. The silent
   *  `history.replace` avoids adding a second undo step the user didn't
   *  initiate. */
  private async produceFinalCopy(image: TracingImage, serverStorage: boolean) {
    try {
      const { blob, type } = await renderTracingImageFinal(image);
      let patch: Partial<TracingImage>;
      if (serverStorage) {
        const res = await fetch("/api/images", {
          method: "POST",
          headers: { "content-type": type },
          body: blob,
        });
        if (!res.ok) return;
        patch = { finalUrl: ((await res.json()) as UploadedImage).url };
      } else {
        const finalUrl = await blobToDataUrl(blob);
        patch = {
          finalUrl,
          url: finalUrl,
          quad: [
            { x: 0, y: 0 },
            { x: 1, y: 0 },
            { x: 1, y: 1 },
            { x: 0, y: 1 },
          ],
        };
      }
      const proj = this.ctx.history.state;
      this.ctx.history.replace({
        ...proj,
        tracingImages: projectTracingImages(proj).map((img) =>
          img.id === image.id ? { ...img, ...patch } : img,
        ),
      });
    } catch {
      // Leave finalUrl unset — the original re-derives the crop on demand.
    }
  }

  /* ------------------------------------------------------------------ *
   *  Camera capture (issue #955)
   * ------------------------------------------------------------------ */

  /** Open the live camera view (issue #955) — for capturing the photo
   *  in place instead of uploading one, e.g. from a camera mounted over
   *  the workbench. Requests the highest resolution the device offers
   *  (tracing wants detail); once permission lands, the device picker is
   *  populated so a multi-camera setup (laptop lid + workbench arm) can
   *  switch. Any failure (no camera, permission denied) reports in the
   *  status line and returns to the pick step. */
  private async enterCameraStage(): Promise<void> {
    const status = this.host.querySelector(
      ".image-upload-status",
    ) as HTMLElement | null;
    this.setStep("camera");
    if (status) {
      status.textContent = "Starting camera…";
      status.dataset.state = "uploading";
    }
    const started = await this.startCameraStream();
    if (!started) {
      this.setStep("pick");
      return;
    }
    if (status) {
      status.textContent = "";
      delete status.dataset.state;
    }
    await this.populateCameraPicker();
  }

  /** Start (or switch to) the camera stream. `deviceId` absent → let the
   *  browser pick a default. Returns false on failure, reporting why.
   *
   *  The stream is always requested in the camera's native landscape
   *  frame at the highest resolution it offers: the `aspectRatio` ideal
   *  pins the intent, because bare 3840×2160 ideals can make the
   *  browser's fitness-distance mode selection pick a rotated portrait
   *  mode on cameras that expose one (MacBook Center Stage, Continuity
   *  Camera). Orientation is OUR transform on top — see
   *  `cameraRotation`. */
  private async startCameraStream(deviceId?: string): Promise<boolean> {
    this.stopCameraStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
          width: { ideal: 3840 },
          height: { ideal: 2160 },
          aspectRatio: { ideal: 16 / 9 },
        },
        audio: false,
      });
      this.cameraStream = stream;
      const video = this.host.querySelector(
        ".camera-video",
      ) as HTMLVideoElement | null;
      if (video) video.srcObject = stream;
      return true;
    } catch (e) {
      const status = this.host.querySelector(
        ".image-upload-status",
      ) as HTMLElement | null;
      if (status) {
        const err = e as DOMException;
        status.textContent =
          err?.name === "NotAllowedError"
            ? "Camera access was denied — allow it in the browser and try again."
            : err?.name === "NotFoundError"
              ? "No camera found on this device."
              : "Couldn't start the camera — try again or upload a photo instead.";
        status.dataset.state = "error";
      }
      return false;
    }
  }

  /** Advance the preview rotation a quarter turn (0 → 90 → 180 → 270 →
   *  0) and re-fit the video inside the fixed-height preview box. Pure
   *  client-side transform — the stream is untouched, so it works the
   *  same on every camera. */
  private rotateCameraPreview(): void {
    this.cameraRotation = ((this.cameraRotation + 90) % 360) as
      | 0
      | 90
      | 180
      | 270;
    this.applyCameraRotation();
  }

  /** Size + rotate the <video> so the ROTATED frame fits the preview
   *  box: a quarter-turned element's visual box has its width and height
   *  swapped, so for 90°/270° the element is laid out box-height wide ×
   *  box-width tall before the transform spins it into place. The box
   *  itself keeps its fixed height, so the dialog never shifts. */
  private applyCameraRotation(): void {
    const video = this.host.querySelector(
      ".camera-video",
    ) as HTMLVideoElement | null;
    const box = video?.parentElement;
    if (!video || !box) return;
    const quarter = this.cameraRotation % 180 !== 0;
    video.style.width = quarter ? `${box.clientHeight}px` : "";
    video.style.height = quarter ? `${box.clientWidth}px` : "";
    video.style.transform = this.cameraRotation
      ? `rotate(${this.cameraRotation}deg)`
      : "";
    video.dataset.rotation = String(this.cameraRotation);
  }

  /** Fill the device picker with the available cameras. Runs after
   *  permission is granted (labels are empty before that) and selects
   *  the device backing the live stream. */
  private async populateCameraPicker(): Promise<void> {
    const select = this.host.querySelector(
      ".camera-device",
    ) as HTMLSelectElement | null;
    if (!select) return;
    let devices: MediaDeviceInfo[] = [];
    try {
      devices = (await navigator.mediaDevices.enumerateDevices()).filter(
        (d) => d.kind === "videoinput",
      );
    } catch {
      /* picker stays empty — the default stream still works */
    }
    select.innerHTML = "";
    devices.forEach((d, i) => {
      const opt = document.createElement("option");
      opt.value = d.deviceId;
      opt.textContent = d.label || `Camera ${i + 1}`;
      select.appendChild(opt);
    });
    // A single camera needs no picker.
    select.hidden = devices.length < 2;
    const activeId = this.cameraStream
      ?.getVideoTracks()[0]
      ?.getSettings().deviceId;
    if (activeId) select.value = activeId;
    if (!select.dataset.wired) {
      select.dataset.wired = "1";
      select.addEventListener("change", () => {
        void this.startCameraStream(select.value);
      });
    }
  }

  /** Grab the current video frame, encode it as a JPEG (photos — #547's
   *  widely-available default), stop the camera, and feed it through the
   *  exact same ingestion path as a picked file: preview, keystone, and
   *  the background upload all behave identically. */
  private async captureCameraFrame(): Promise<void> {
    const video = this.host.querySelector(
      ".camera-video",
    ) as HTMLVideoElement | null;
    if (!video || !video.videoWidth || !video.videoHeight) return;
    // Bake the preview rotation into the captured pixels so what the
    // user sees is exactly what enters the wizard — a quarter turn swaps
    // the output canvas's dimensions.
    const rot = this.cameraRotation;
    const w = video.videoWidth;
    const h = video.videoHeight;
    const canvas = document.createElement("canvas");
    canvas.width = rot % 180 !== 0 ? h : w;
    canvas.height = rot % 180 !== 0 ? w : h;
    const ctx = canvas.getContext("2d")!;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.drawImage(video, -w / 2, -h / 2);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92),
    );
    if (!blob) return;
    this.stopCameraStream();
    this.handleFile(
      new File([blob], "camera-capture.jpg", { type: blob.type }),
    );
  }

  /** Stop every camera track and detach the preview. Safe to call
   *  whenever — close(), Back, re-open, and capture all route through
   *  here so a webcam light never stays on after the view is gone. */
  private stopCameraStream(): void {
    if (this.cameraStream) {
      for (const track of this.cameraStream.getTracks()) track.stop();
      this.cameraStream = null;
    }
    const video = this.host.querySelector(
      ".camera-video",
    ) as HTMLVideoElement | null;
    if (video) video.srcObject = null;
  }

  /** Validate, then preview + keystone immediately. A signed-in session
   *  also uploads in the background; a logged-out one keeps the image
   *  local to the device (issue #496). */
  private handleFile(file: File) {
    const seq = ++this.pickSeq;
    const status = this.host.querySelector(
      ".image-upload-status",
    ) as HTMLElement | null;
    if (!status) return;

    if (!IMAGE_TYPES.has(file.type)) {
      status.textContent = `"${file.name}" isn't a supported image — use PNG, JPEG, WebP, GIF, or AVIF.`;
      status.dataset.state = "error";
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      status.textContent = `"${file.name}" is ${Math.round(file.size / 1024 / 1024)} MB — the limit is ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`;
      status.dataset.state = "error";
      return;
    }

    // Show the photo instantly from the local file…
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = URL.createObjectURL(file);
    this.fileName = file.name;
    this.enterKeystoneStage(this.previewUrl, seq);

    // Re-picking abandons a previously-landed upload — delete it from the
    // server rather than leave an orphan behind (#543).
    if (this.uploaded) {
      deleteUploadedImage(this.uploaded.key);
      writePendingUpload(null);
    }
    this.uploaded = null;
    status.textContent = "Checking your account…";
    status.dataset.state = "uploading";
    // …then upload only when server storage applies: signed in AND the
    // "Server image storage" flag is on (#536). Flag off keeps a signed-in
    // user's image local, same as an anonymous one — only the wording
    // differs.
    void this.ensureLoginChecked().then((loggedIn) => {
      if (seq !== this.pickSeq) return; // superseded by a newer pick
      if (this.serverStorage) {
        status.textContent = "Uploading…";
        status.dataset.state = "uploading";
        void this.upload(file, seq, status);
      } else {
        status.textContent = loggedIn
          ? "Server image storage is off — the image is stored inside this project, not your account."
          : "Not signed in — the image is stored inside this project, not your account.";
        status.dataset.state = "local";
        this.syncAddButton();
      }
    });
  }

  /** Probe `/api/me` once per dialog open, caching the result. Treats any
   *  failure as logged-out — better to keep an image local than to lose a
   *  silent upload. */
  private ensureLoginChecked(): Promise<boolean> {
    if (!this.loginChecked) {
      this.loginChecked = fetch("/api/me")
        .then((r) =>
          r.ok ? (r.json() as Promise<{ accountId?: string | null }>) : null,
        )
        .then((body) => {
          this.loggedIn = !!body?.accountId;
          this.serverStorage =
            this.loggedIn && isFeatureEnabled("image-server-storage");
          return this.loggedIn;
        })
        .catch(() => {
          this.loggedIn = false;
          this.serverStorage = false;
          return false;
        });
    }
    return this.loginChecked;
  }

  /* ------------------------------------------------------------------ *
   *  Step 2 — keystone (issue #486)
   * ------------------------------------------------------------------ */

  /** Swap the drop zone for the keystone editor: the photo with
   *  draggable corner handles, plus the live de-skewed preview. */
  private enterKeystoneStage(url: string, seq: number) {
    const img = this.host.querySelector(
      ".keystone-image",
    ) as HTMLImageElement | null;
    if (!img) return;
    img.onload = () => {
      if (seq !== this.pickSeq) return;
      // Rasterize once into the capped sampling buffer the warp reads.
      const scale = Math.min(
        1,
        SAMPLE_MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight),
      );
      const sw = Math.max(1, Math.round(img.naturalWidth * scale));
      const sh = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = sw;
      canvas.height = sh;
      const c2d = canvas.getContext("2d")!;
      c2d.drawImage(img, 0, 0, sw, sh);
      this.sample = c2d.getImageData(0, 0, sw, sh);
      this.quad = defaultQuad(sw, sh);
      // A new photo invalidates any de-skew / calibration / crop done on
      // the previous one.
      this.deskew = null;
      this.deskewLayout = null;
      this.hLine = null;
      this.vLine = null;
      this.crop = null;

      this.setStep("keystone");
      this.layoutKeystoneOverlay();
      this.updateKeystoneLoupe(this.quad[0]);
      this.scheduleWarp();
    };
    img.src = url;
  }

  /** Size the SVG overlay to the displayed image and place the quad +
   *  handles (display coordinates track the sampling raster's). */
  private layoutKeystoneOverlay() {
    const img = this.host.querySelector(
      ".keystone-image",
    ) as HTMLImageElement | null;
    const svg = this.host.querySelector(
      ".keystone-overlay",
    ) as SVGSVGElement | null;
    if (!img || !svg || !this.quad || !this.sample) return;
    const w = img.clientWidth;
    svg.setAttribute(
      "viewBox",
      `0 0 ${this.sample.width} ${this.sample.height}`,
    );
    const poly = svg.querySelector(".keystone-quad") as SVGPolygonElement;
    poly.setAttribute(
      "points",
      this.quad.map((p) => `${p.x},${p.y}`).join(" "),
    );
    svg.querySelectorAll(".keystone-handle").forEach((el, i) => {
      const p = this.quad![i]!;
      el.setAttribute("cx", String(p.x));
      el.setAttribute("cy", String(p.y));
      // Handle radius is in viewBox units — scale it so it stays ~9
      // screen pixels regardless of the photo's resolution.
      el.setAttribute("r", String((9 * this.sample!.width) / Math.max(1, w)));
    });
  }

  private wireKeystoneHandles(modal: HTMLElement) {
    const svg = modal.querySelector(".keystone-overlay") as SVGSVGElement;
    let dragging = -1;
    svg.addEventListener("pointerdown", (ev) => {
      const handle = (ev.target as Element).closest(".keystone-handle");
      if (!handle) return;
      dragging = Number((handle as SVGElement).dataset.corner);
      svg.setPointerCapture(ev.pointerId);
      ev.preventDefault();
      if (this.quad)
        this.updateKeystoneLoupe(this.quad[dragging as 0 | 1 | 2 | 3]);
    });
    svg.addEventListener("pointermove", (ev) => {
      if (dragging < 0 || !this.quad || !this.sample) return;
      const rect = svg.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * this.sample.width;
      const y = ((ev.clientY - rect.top) / rect.height) * this.sample.height;
      const p: Point = {
        x: Math.max(0, Math.min(this.sample.width, x)),
        y: Math.max(0, Math.min(this.sample.height, y)),
      };
      this.quad[dragging as 0 | 1 | 2 | 3] = p;
      this.layoutKeystoneOverlay();
      this.updateKeystoneLoupe(p);
      this.scheduleWarp();
    });
    const release = () => {
      dragging = -1;
    };
    svg.addEventListener("pointerup", release);
    svg.addEventListener("pointercancel", release);
  }

  /** Re-warp at most once per animation frame while a handle drags. */
  private scheduleWarp() {
    if (this.warpQueued) return;
    this.warpQueued = true;
    requestAnimationFrame(() => {
      this.warpQueued = false;
      this.renderDeskewPreview();
    });
  }

  private renderDeskewPreview() {
    const canvas = this.host.querySelector(
      ".keystone-preview",
    ) as HTMLCanvasElement | null;
    if (!canvas || !this.quad || !this.sample) return;
    const { width, height } = deskewSize(this.quad, PREVIEW_MAX_DIMENSION);
    canvas.width = width;
    canvas.height = height;
    const c2d = canvas.getContext("2d")!;
    const out = c2d.createImageData(width, height);
    warpQuadToRect(this.sample, this.quad, out);
    c2d.putImageData(out, 0, 0);
  }

  /* ------------------------------------------------------------------ *
   *  Magnifier loupe (issue #497)
   * ------------------------------------------------------------------ */

  /** Paint a 5×-zoomed close-up of `source` centred on (`ptX`,`ptY`) —
   *  given in `source`'s own pixel coordinates — with a crosshair on the
   *  exact point. `displayScale` is source-px → on-screen-px, so the
   *  loupe magnifies 5× beyond what the user already sees. The whole
   *  source is drawn under a transform and clipped to the canvas, so the
   *  crosshair lands precisely on the point even at the image's edges. */
  private drawLoupe(
    loupe: HTMLCanvasElement,
    source: CanvasImageSource,
    ptX: number,
    ptY: number,
    displayScale: number,
  ) {
    const ctx = loupe.getContext("2d");
    if (!ctx || !Number.isFinite(displayScale) || displayScale <= 0) return;
    const size = loupe.width;
    ctx.save();
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, size, size);
    ctx.beginPath();
    ctx.rect(0, 0, size, size);
    ctx.clip();
    ctx.imageSmoothingEnabled = false;
    ctx.translate(size / 2, size / 2);
    ctx.scale(LOUPE_ZOOM * displayScale, LOUPE_ZOOM * displayScale);
    ctx.translate(-ptX, -ptY);
    ctx.drawImage(source, 0, 0);
    ctx.restore();
    // White crosshair with a small centre gap so the exact pixel stays
    // visible. A thin dark backing stroke underneath keeps the white lines
    // legible over light areas of the photo too.
    const c = size / 2;
    const gap = 5;
    const crosshairPath = () => {
      ctx.beginPath();
      ctx.moveTo(c, 0);
      ctx.lineTo(c, c - gap);
      ctx.moveTo(c, c + gap);
      ctx.lineTo(c, size);
      ctx.moveTo(0, c);
      ctx.lineTo(c - gap, c);
      ctx.moveTo(c + gap, c);
      ctx.lineTo(size, c);
    };
    ctx.save();
    // Dark backing first, then a solid 2px white line on top. The white
    // is 2px and centred on integer coordinates so it covers whole pixels
    // — a 1px line straddles the pixel boundary and anti-aliases to grey.
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#000";
    crosshairPath();
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#fff";
    crosshairPath();
    ctx.stroke();
    ctx.restore();
  }

  /** Loupe over the keystone photo (the `<img>` at natural resolution).
   *  `p` is in the sampling raster's coordinates, like the quad. */
  private updateKeystoneLoupe(p: Point) {
    const loupe = this.host.querySelector(
      ".keystone-loupe",
    ) as HTMLCanvasElement | null;
    const img = this.host.querySelector(
      ".keystone-image",
    ) as HTMLImageElement | null;
    if (!loupe || !img || !this.sample || !img.naturalWidth || !img.clientWidth)
      return;
    const natScale = img.naturalWidth / this.sample.width;
    const displayScale = img.clientWidth / img.naturalWidth;
    this.drawLoupe(loupe, img, p.x * natScale, p.y * natScale, displayScale);
  }

  /** Loupe over the de-skewed calibrate canvas. `p` is in deskew-raster
   *  coordinates, like the reference lines. */
  private updateCalibrateLoupe(p: Point) {
    const loupe = this.host.querySelector(
      ".calibrate-loupe",
    ) as HTMLCanvasElement | null;
    const display = this.host.querySelector(
      ".calibrate-image",
    ) as HTMLCanvasElement | null;
    if (!loupe || !display || !this.deskew || !display.clientWidth) return;
    const displayScale = display.clientWidth / this.deskew.width;
    this.drawLoupe(loupe, this.deskew, p.x, p.y, displayScale);
  }

  /* ------------------------------------------------------------------ *
   *  Step 3 — size calibration (issue #487)
   * ------------------------------------------------------------------ */

  /** Render the working de-skew once and lay the two reference lines
   *  over it with their default placement. */
  private enterCalibrateStage() {
    if (!this.quad || !this.sample) return;
    // De-skew the WHOLE photo, not just the keystone quad (issue #492):
    // the quad defines the rectification, but the user's known-length
    // reference (ruler, mat grid) may sit anywhere in the frame, so the
    // calibration step shows the full image. The quad maps onto a
    // rectWidth×rectHeight rectangle at the origin of "rectified space";
    // the rest of the photo lands around it inside `bounds`.
    const { width: rectWidth, height: rectHeight } = deskewSize(
      this.quad,
      DESKEW_MAX_DIMENSION,
    );
    const bounds = fullDeskewBounds(
      this.quad,
      this.sample.width,
      this.sample.height,
      rectWidth,
      rectHeight,
    );
    // A small quad in a big photo (or strong perspective) can make the
    // rectified frame huge — scale the OUTPUT down rather than warping
    // millions of pixels.
    const outScale = Math.min(
      1,
      FULL_DESKEW_MAX_DIMENSION / Math.max(bounds.w, bounds.h),
    );
    const width = Math.max(1, Math.round(bounds.w * outScale));
    const height = Math.max(1, Math.round(bounds.h * outScale));
    // The source region that fills the output is the preimage of the
    // bounds rectangle — its corners pushed through the keystone map.
    const m = unitSquareToQuad(this.quad);
    const src = (bx: number, by: number): Point =>
      applyMatrix3(m, { x: bx / rectWidth, y: by / rectHeight });
    const boundsQuad: Quad = [
      src(bounds.x, bounds.y),
      src(bounds.x + bounds.w, bounds.y),
      src(bounds.x + bounds.w, bounds.y + bounds.h),
      src(bounds.x, bounds.y + bounds.h),
    ];
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const c2d = canvas.getContext("2d")!;
    const out = c2d.createImageData(width, height);
    warpQuadToRect(this.sample, boundsQuad, out);
    c2d.putImageData(out, 0, 0);
    this.deskew = canvas;
    // Recorded so later stages can convert display coordinates back to
    // quad-rect space (and from there through the keystone map).
    this.deskewLayout = {
      scale: outScale,
      minX: bounds.x,
      minY: bounds.y,
      rectWidth,
      rectHeight,
    };

    // Default placement: inside the quad's own rectangle (the part of
    // the photo the user explicitly marked), not the expanded frame.
    // Kept if the user comes Back from this step and returns — line
    // edits are only reset by a new photo.
    if (!this.hLine || !this.vLine) {
      const qx = (u: number) => (u * rectWidth - bounds.x) * outScale;
      const qy = (v: number) => (v * rectHeight - bounds.y) * outScale;
      this.hLine = [
        { x: qx(0.15), y: qy(0.45) },
        { x: qx(0.85), y: qy(0.45) },
      ];
      this.vLine = [
        { x: qx(0.6), y: qy(0.15) },
        { x: qx(0.6), y: qy(0.85) },
      ];
    }

    const display = this.host.querySelector(
      ".calibrate-image",
    ) as HTMLCanvasElement | null;
    if (display) {
      display.width = width;
      display.height = height;
      display.getContext("2d")!.drawImage(canvas, 0, 0);
    }
    this.setStep("calibrate");
    this.layoutCalibrateOverlay();
    this.updateCalibrateLoupe(this.hLine[0]);
    this.updateCalibrateScale();
  }

  private layoutCalibrateOverlay() {
    const svg = this.host.querySelector(
      ".calibrate-overlay",
    ) as SVGSVGElement | null;
    const display = this.host.querySelector(
      ".calibrate-image",
    ) as HTMLCanvasElement | null;
    if (!svg || !display || !this.deskew || !this.hLine || !this.vLine) return;
    svg.setAttribute(
      "viewBox",
      `0 0 ${this.deskew.width} ${this.deskew.height}`,
    );
    const r = (9 * this.deskew.width) / Math.max(1, display.clientWidth);
    const place = (line: RefLine, which: "h" | "v") => {
      const el = svg.querySelector(
        `.calibrate-line-${which}`,
      ) as SVGLineElement;
      el.setAttribute("x1", String(line[0].x));
      el.setAttribute("y1", String(line[0].y));
      el.setAttribute("x2", String(line[1].x));
      el.setAttribute("y2", String(line[1].y));
      svg
        .querySelectorAll(`.calibrate-handle[data-line="${which}"]`)
        .forEach((h, i) => {
          h.setAttribute("cx", String(line[i]!.x));
          h.setAttribute("cy", String(line[i]!.y));
          h.setAttribute("r", String(r));
        });
    };
    place(this.hLine, "h");
    place(this.vLine, "v");
  }

  private wireCalibrateHandles(modal: HTMLElement) {
    const svg = modal.querySelector(".calibrate-overlay") as SVGSVGElement;
    let dragging: { line: "h" | "v"; end: 0 | 1 } | null = null;
    svg.addEventListener("pointerdown", (ev) => {
      const handle = (ev.target as Element).closest(".calibrate-handle");
      if (!handle) return;
      dragging = {
        line: (handle as SVGElement).dataset.line as "h" | "v",
        end: Number((handle as SVGElement).dataset.end) as 0 | 1,
      };
      svg.setPointerCapture(ev.pointerId);
      ev.preventDefault();
      const line = dragging.line === "h" ? this.hLine : this.vLine;
      if (line) this.updateCalibrateLoupe(line[dragging.end]);
    });
    svg.addEventListener("pointermove", (ev) => {
      if (!dragging || !this.deskew) return;
      const rect = svg.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * this.deskew.width;
      const y = ((ev.clientY - rect.top) / rect.height) * this.deskew.height;
      const p: Point = {
        x: Math.max(0, Math.min(this.deskew.width, x)),
        y: Math.max(0, Math.min(this.deskew.height, y)),
      };
      const line = dragging.line === "h" ? this.hLine : this.vLine;
      if (!line) return;
      line[dragging.end] = p;
      // The keystone step already rectified the image, so each reference
      // line measures a single axis and must stay axis-aligned: moving
      // one end of the horizontal line vertically carries the other end
      // with it, and likewise for the vertical line horizontally.
      const other = line[dragging.end === 0 ? 1 : 0];
      if (dragging.line === "h") other.y = p.y;
      else other.x = p.x;
      this.layoutCalibrateOverlay();
      this.updateCalibrateLoupe(p);
      this.updateCalibrateScale();
    });
    const release = () => {
      dragging = null;
    };
    svg.addEventListener("pointerup", release);
    svg.addEventListener("pointercancel", release);
  }

  /** The two length fields are the app's standard paired mm/in inputs
   *  (fraction-aware, unit-suffix parsing, primary unit first per the
   *  units preference) — `onChange` always reports millimetres.
   *  `liveCommit` keeps the scale readout tracking every keystroke;
   *  it only mutates dialog state, never project history. */
  private wireCalibrateInputs(modal: HTMLElement) {
    const mount = (sel: string, set: (mm: number | null) => void) => {
      const field = modal.querySelector(sel) as HTMLElement;
      const { row } = mmInchInputs(
        NaN,
        (mm) => {
          set(mm > 0 ? mm : null);
          this.updateCalibrateScale();
        },
        { liveCommit: true },
      );
      field.appendChild(row);
    };
    mount(".calibrate-field-h", (mm) => {
      this.hMm = mm;
    });
    mount(".calibrate-field-v", (mm) => {
      this.vMm = mm;
    });
  }

  /** mm-per-pixel on each axis, or null until that axis has both a
   *  drawn line and an entered length. */
  private calibrationScale(): { x: number | null; y: number | null } {
    const span = (line: RefLine | null, axis: "x" | "y") =>
      line ? Math.abs(line[1][axis] - line[0][axis]) : 0;
    const h = span(this.hLine, "x");
    const v = span(this.vLine, "y");
    return {
      x: this.hMm !== null && h > 1 ? this.hMm / h : null,
      y: this.vMm !== null && v > 1 ? this.vMm / v : null,
    };
  }

  private updateCalibrateScale() {
    const out = this.host.querySelector(
      ".calibrate-scale",
    ) as HTMLElement | null;
    if (!out) return;
    const { x, y } = this.calibrationScale();
    if (x === null && y === null) {
      out.textContent =
        "Enter both lengths to calibrate the image's real-world scale.";
      out.dataset.state = "empty";
      return;
    }
    const fmt = (v: number | null) =>
      v === null ? "—" : `${v.toFixed(3)} mm/px`;
    out.textContent = `Scale: ${fmt(x)} horizontal × ${fmt(y)} vertical`;
    out.dataset.state = x !== null && y !== null ? "done" : "partial";
    // Calibration readiness gates this step's Next button (#499) and the
    // crop step's "Add to canvas" button.
    this.syncNextButton();
    this.syncAddButton();
  }

  /* ------------------------------------------------------------------ *
   *  Step 4 — crop (issue #488)
   * ------------------------------------------------------------------ */

  /** Show the de-skewed image with the crop rectangle over it. The crop
   *  starts inset 10% from every side (issue #508) so the corner handles
   *  sit off the image edges and are easy to grab; corner drags trim it,
   *  dragging inside moves it, and everything outside is dimmed. */
  private enterCropStage() {
    if (!this.deskew) return;
    const display = this.host.querySelector(
      ".crop-image",
    ) as HTMLCanvasElement | null;
    if (display) {
      display.width = this.deskew.width;
      display.height = this.deskew.height;
      display.getContext("2d")!.drawImage(this.deskew, 0, 0);
    }
    if (!this.crop) {
      const { width: W, height: H } = this.deskew;
      this.crop = {
        x: W * CROP_DEFAULT_INSET,
        y: H * CROP_DEFAULT_INSET,
        w: W * (1 - 2 * CROP_DEFAULT_INSET),
        h: H * (1 - 2 * CROP_DEFAULT_INSET),
      };
    }
    this.setStep("crop");
    this.layoutCropOverlay();
  }

  private layoutCropOverlay() {
    const svg = this.host.querySelector(
      ".crop-overlay",
    ) as SVGSVGElement | null;
    const display = this.host.querySelector(
      ".crop-image",
    ) as HTMLCanvasElement | null;
    if (!svg || !display || !this.deskew || !this.crop) return;
    const { width: W, height: H } = this.deskew;
    const { x, y, w, h } = this.crop;
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
    const shade = svg.querySelector(".crop-shade") as SVGPathElement;
    // Even-odd ring: full frame minus the crop window.
    shade.setAttribute(
      "d",
      `M0 0H${W}V${H}H0Z M${x} ${y}H${x + w}V${y + h}H${x}Z`,
    );
    const rect = svg.querySelector(".crop-rect") as SVGRectElement;
    rect.setAttribute("x", String(x));
    rect.setAttribute("y", String(y));
    rect.setAttribute("width", String(w));
    rect.setAttribute("height", String(h));
    const r = (9 * W) / Math.max(1, display.clientWidth);
    const corners: Point[] = [
      { x, y },
      { x: x + w, y },
      { x: x + w, y: y + h },
      { x, y: y + h },
    ];
    svg.querySelectorAll(".crop-handle").forEach((el, i) => {
      el.setAttribute("cx", String(corners[i]!.x));
      el.setAttribute("cy", String(corners[i]!.y));
      el.setAttribute("r", String(r));
    });
  }

  private wireCropHandles(modal: HTMLElement) {
    const svg = modal.querySelector(".crop-overlay") as SVGSVGElement;
    /** Corner index being resized, or "move" with the grab offset. */
    let drag: { corner: 0 | 1 | 2 | 3 } | { dx: number; dy: number } | null =
      null;
    const toRaster = (ev: PointerEvent): Point => {
      const rect = svg.getBoundingClientRect();
      return {
        x: ((ev.clientX - rect.left) / rect.width) * this.deskew!.width,
        y: ((ev.clientY - rect.top) / rect.height) * this.deskew!.height,
      };
    };
    svg.addEventListener("pointerdown", (ev) => {
      if (!this.deskew || !this.crop) return;
      const target = ev.target as Element;
      const handle = target.closest(".crop-handle");
      if (handle) {
        drag = {
          corner: Number((handle as SVGElement).dataset.corner) as
            | 0
            | 1
            | 2
            | 3,
        };
      } else if (target.closest(".crop-rect")) {
        const p = toRaster(ev);
        drag = { dx: p.x - this.crop.x, dy: p.y - this.crop.y };
      } else {
        return;
      }
      svg.setPointerCapture(ev.pointerId);
      ev.preventDefault();
    });
    svg.addEventListener("pointermove", (ev) => {
      if (!drag || !this.deskew || !this.crop) return;
      const { width: W, height: H } = this.deskew;
      const p = toRaster(ev);
      const c = this.crop;
      if ("corner" in drag) {
        // Opposite corner stays anchored; the dragged one follows the
        // pointer, clamped to the frame and to the minimum side.
        const right = c.x + c.w;
        const bottom = c.y + c.h;
        const px = Math.max(0, Math.min(W, p.x));
        const py = Math.max(0, Math.min(H, p.y));
        if (drag.corner === 0 || drag.corner === 3) {
          c.x = Math.min(px, right - MIN_CROP_SIDE);
          c.w = right - c.x;
        } else {
          c.w = Math.max(MIN_CROP_SIDE, px - c.x);
          if (c.x + c.w > W) c.w = W - c.x;
        }
        if (drag.corner === 0 || drag.corner === 1) {
          c.y = Math.min(py, bottom - MIN_CROP_SIDE);
          c.h = bottom - c.y;
        } else {
          c.h = Math.max(MIN_CROP_SIDE, py - c.y);
          if (c.y + c.h > H) c.h = H - c.y;
        }
      } else {
        c.x = Math.max(0, Math.min(W - c.w, p.x - drag.dx));
        c.y = Math.max(0, Math.min(H - c.h, p.y - drag.dy));
      }
      this.layoutCropOverlay();
    });
    const release = () => {
      drag = null;
    };
    svg.addEventListener("pointerup", release);
    svg.addEventListener("pointercancel", release);
  }

  /* ------------------------------------------------------------------ *
   *  Upload (issue #484)
   * ------------------------------------------------------------------ */

  private async upload(file: File, seq: number, status: HTMLElement) {
    try {
      // Modern-format pass (#547): PNG originals re-encode to WebP (capped
      // at the source dimension the warp ever samples) before leaving the
      // device; everything else uploads as-is.
      const prepared = await prepareTracingUpload(file);
      if (seq !== this.pickSeq) return; // superseded during the re-encode
      const res = await fetch("/api/images", {
        method: "POST",
        headers: { "content-type": prepared.type },
        body: prepared.body,
      });
      if (!res.ok) {
        if (seq !== this.pickSeq) return; // superseded by a newer pick
        const body = (await res.json().catch(() => null)) as {
          error?: string;
        } | null;
        throw new Error(body?.error ?? `Upload failed (HTTP ${res.status}).`);
      }
      const uploaded = (await res.json()) as UploadedImage;
      if (seq !== this.pickSeq) {
        // Superseded by a newer pick (or the dialog closed) while the
        // request was in flight — the landed object has no owner, so
        // delete it rather than leave an orphan in R2 (#543).
        deleteUploadedImage(uploaded.key);
        return;
      }
      this.uploaded = uploaded;
      // Survive an accidental reload (#543): remember the landed upload
      // until it's either committed to the canvas or deliberately
      // discarded by closing the dialog.
      writePendingUpload({ ...uploaded, fileName: this.fileName ?? "photo" });
      status.textContent = "Uploaded.";
      status.dataset.state = "done";
      this.syncAddButton();
    } catch (e) {
      if (seq !== this.pickSeq) return;
      status.textContent =
        e instanceof TypeError
          ? "Upload failed — check your connection and pick the file again to retry."
          : (e as Error).message;
      status.dataset.state = "error";
    }
  }

  /** Re-open the dialog after an accidental page reload when an upload
   *  landed on the server but was never added to the canvas (#543). The
   *  pending record in localStorage names the uploaded object; if it's
   *  still fetchable the wizard resumes at the keystone step with the
   *  uploaded photo (corner tweaks don't survive the reload — the upload
   *  does), and the usual lifecycle applies from there: commit keeps it,
   *  closing the dialog deletes it. A stale record (object deleted
   *  elsewhere, different account) is dropped silently without opening
   *  anything. */
  async restorePendingUpload(): Promise<void> {
    const entry = readPendingUpload();
    if (!entry) return;
    let blob: Blob;
    try {
      const res = await fetch(entry.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      blob = await res.blob();
    } catch {
      writePendingUpload(null);
      return;
    }
    this.open();
    const seq = ++this.pickSeq;
    this.previewUrl = URL.createObjectURL(blob);
    this.fileName = entry.fileName;
    this.enterKeystoneStage(this.previewUrl, seq);
    this.uploaded = { key: entry.key, url: entry.url };
    const status = this.host.querySelector(
      ".image-upload-status",
    ) as HTMLElement | null;
    if (status) {
      status.textContent = "Uploaded — restored after reload.";
      status.dataset.state = "done";
    }
    this.syncAddButton();
  }

  private handleBackdrop = (ev: Event) => {
    if (ev.target === this.host) this.close();
  };

  close() {
    // Camera view open at close time → stop the tracks (webcam light off).
    this.stopCameraStream();
    // An upload that landed but was never added to the canvas is
    // abandoned by closing the dialog — delete it from the server and
    // forget the reload-survival record (#543). addToCanvas() clears
    // `uploaded` before calling close(), so committed images are safe.
    if (this.uploaded) {
      deleteUploadedImage(this.uploaded.key);
      writePendingUpload(null);
      this.uploaded = null;
    }
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
    this.pickSeq++;
    this.quad = null;
    this.sample = null;
    this.deskew = null;
    this.deskewLayout = null;
    this.hLine = null;
    this.vLine = null;
    this.crop = null;
    this.host.classList.remove("open");
    this.host.innerHTML = "";
    this.host.removeEventListener("click", this.handleBackdrop);
  }
}
